<template>
  <div
    ref="hero"
    :class="classes"
  >
    <div :class="bem('section', { ratio: true })">
      <div
        ref="canvas"
        :class="bem('canvas', { useVariablePadding: !matchHeightToContent && !inSidebar })"
        :style="!matchHeightToContent && !inSidebar ? getRatio : null"
      >
        <div
          ref="canvasInner"
          :class="bem('canvas-inner', { bannerHeightAuto })"
        >
          <DatoCmsImage
            :class="bem('background-image', { desktop: true, mobile: !mobileImage })"
            :data="image.responsiveImage"
            layout="fill"
            object-fit="cover"
            :priority="isFirst"
            :object-position="`${image.focalPoint.x * 100}% ${
              image.focalPoint.y * 100
            }%`"
            :sizes="fullWidth ? '100vw' : '(max-width: 800px) 100vw, 1200px'"
          />

          <BaseOverlay
            :class="bem('overlay')"
            :overlay-opacity="overlayOpacity"
          />

          <DatoCmsImage
            v-if="mobileImage"
            :class="bem('background-image', { mobile: true })"
            :data="mobileImage.responsiveImage"
            layout="fill"
            object-fit="cover"
            :priority="isFirst"
            :object-position="`${mobileImage.focalPoint.x * 100}% ${
              mobileImage.focalPoint.y * 100
            }%`"
            :sizes="fullWidth ? '100vw' : '(max-width: 800px) 100vw, 1200px'"
          />

          <BaseOverlay
            :class="bem('mobile-overlay')"
            :overlay-opacity="mobileOverlayOpacity"
          />
        </div>
      </div>
    </div>

    <div :class="[bem('section'), bem('content-section', { align: contentAlignment })]">
      <div :class="bem('content')">
        <slot />
      </div>
    </div>
  </div>
</template>

<script>
import { Image as DatoCmsImage } from 'vue-datocms'
import aspectRatio from '~/assets/js/constants/aspectRatio'

export default {
  name: 'BaseBanner',

  components: {
    DatoCmsImage
  },

  props: {
    aspectRatio: {
      type: String,
      default: 'auto',
      validator: value => aspectRatio.includes(value)
    },

    image: {
      type: Object,
      required: true
    },

    overlayOpacity: {
      type: Number,
      default: 0
    },

    mobileImage: {
      type: Object,
      default: () => null
    },

    mobileOverlayOpacity: {
      type: Number,
      default: 0
    },

    fullWidth: {
      type: Boolean,
      default: false
    },

    bannerHeightAuto: {
      type: Boolean,
      default: false
    },

    matchHeightToContent: {
      type: Boolean,
      default: false
    },

    minHeight: {
      type: String,
      default: '50vh',
      validator: (value) => {
        return [
          'md', // TODO: remove once old CMS entries with 'md' get updated
          '30vh',
          '50vh',
          '75vh',
          '80vh',
          '100vh'
        ].includes(value)
      }
    },

    isFirst: {
      type: Boolean,
      default: false
    },
    inSidebar: {
      type: Boolean,
      default: false
    },
    disableParallax: {
      type: Boolean,
      default: false
    },
    contentAlignment: {
      type: String,
      default: 'center'
    },
    bottomPadding: {
      type: String,
      default: 'default',
      validator: (value) => {
        return [
          'default',
          'sm',
          'md',
          'lg',
          'xl',
          'xxl'
        ].includes(value)
      }
    }
  },

  setup () {
    const bem = useBEM('BaseBanner')

    return { bem }
  },

  data () {
    return {
      isMounted: false
    }
  },

  computed: {
    classes () {
      return {
        BaseBanner: true,
        'BaseBanner--full-width': this.fullWidth,
        'BaseBanner--dynamic-height': this.matchHeightToContent,
        [`BaseBanner--min-height-${this.minHeight}`]: this.matchHeightToContent,
        'BaseBanner--first': this.isFirst,
        'BaseBanner--no-parallax': this.disableParallax || this.isFirst,
        'BaseBanner--in-sidebar': this.inSidebar,
        [`BaseBanner--bottom-padding-${this.bottomPadding}`]: this.bottomPadding
      }
    },

    getRatio () {
      const ratio = this.aspectRatio.split(':')
      const width = ratio[0]
      const height = ratio[1]

      return {
        '--pt-mobile': `${(4 / 3 * 100)}%`,
        '--pt-desktop': `${(height / width * 100)}%`
      }
    },

    srcsetSizes () {
      return [
        { minWidth: 1500, width: 1500 },
        { minWidth: 1400, width: 1400 },
        { minWidth: 1000, width: 1000 },
        { minWidth: 700, width: 700 },
        { width: 350 }
      ]
    }
  },

  mounted () {
    this.isMounted = true
  },

  unmounted () {
    if (this.canvasTl) this.canvasTl.kill()
    if (this.timeout) clearTimeout(this.timeout)
  }
}
</script>

<style lang="postcss">
.BaseBanner {
  @apply relative flex flex-row flex-nowrap w-full h-full overflow-hidden;

  &:not(.BaseBanner--full-width) {
    @apply rounded-[10px] will-change-transform;
  }

  @screen mdMax {
    &--bottom-padding-sm {
      @apply pb-sm;
    }

    &--bottom-padding-md {
      @apply pb-md;
    }

    &--bottom-padding-lg {
      @apply pb-lg;
    }

    &--bottom-padding-xl {
      @apply pb-xl;
    }

    &--bottom-padding-xxl {
      @apply pb-[22em];
    }
  }

  &__background-image {
    &--desktop {
      @apply mdMax:!hidden md:block;

      &.BaseBanner__background-image--mobile {
        @apply mdMax:!block md:!block;
      }
    }

    &--mobile {
      @apply mdMax:block md:!hidden;
    }
  }
}

.BaseBanner__section {
  @apply grow shrink-0 basis-auto w-full;
}

.BaseBanner__content-section {
  /*
    - Positions content over the ratio container to preserve dimensions.
    - If content exceeds ratio container height the element grows with the content.
  */
  @apply -translate-x-full flex flex-col flex-nowrap justify-center;

  &--align-top {
    @apply justify-start
      pt-sm;
  }

  &--align-bottom {
    @apply justify-end
      pb-sm;
  }
}

.BaseBanner__canvas {
  @apply w-full h-full;

  &--use-variable-padding {
    padding-top: var(--pt-mobile);

    @screen md {
      padding-top: var(--pt-desktop);
    }
  }

  .BaseBanner:not(.BaseBanner--dynamic-height) &:not(&--use-variable-padding) {
    @apply h-0;
    padding-top: calc(16 / 9 * 100%);
  }

  .BaseBanner:not(.BaseBanner--dynamic-height).BaseBanner--in-sidebar &:not(&--use-variable-padding) {
    padding-top: calc(0.72 * 100%);
    @apply !transform-none;
  }

  & img {
    @apply h-full object-cover;
  }
}

.BaseBanner__canvas-inner {
  @apply absolute top-0 left-0 w-full h-full transition-transform duration-75 ease-in-out;
  transform: scale(1) translateZ(0);

  &.BaseBanner__canvas-inner--banner-height-auto {
    @apply md:h-auto;
  }

  .BaseBanner--first &,
  .BaseBanner--no-parallax & {
    @apply h-full;
  }

  .BaseBanner.has-hover & {
    transform: scale(1.025) translateZ(0);
  }

  .BaseBanner--in-sidebar & {
    @apply h-full;
  }
}

.BaseBanner__content {
  --one-column: calc(1 / 12 * (100% - (11 * 32px)) + 32px);

  @apply w-full;
  padding: var(--outer-gutter);

  @screen sm {
    padding: var(--one-column);
  }

  .BaseBanner--full-width & {
    @apply mx-auto;
    max-width: var(--screen-size);
    padding-left: var(--outer-gutter);
    padding-right: var(--outer-gutter);
  }

  .BaseBanner--in-sidebar & {
    @apply pl-sm sm:pl-md md:pl-lg xl:pl-xl
  }

  .is-default-standalone-theme & {
    padding: calc(2 * var(--outer-gutter)) var(--one-column);
  }
}

.BaseBanner--full-width {
  margin-left: calc(0px - var(--outer-gutter));
  margin-right: calc(0px - var(--outer-gutter));

  @screen full {
    margin-left: calc(0px - (((100vw - var(--screen-size)) / 2) + var(--outer-gutter)));
    margin-right: calc(0px - (((100vw - var(--screen-size)) / 2) + var(--outer-gutter)));
  }
}

.BaseBanner--dynamic-height {
  &.BaseBanner--min-height-30vh {
    min-height: 30vh;
  }

  &.BaseBanner--min-height-50vh {
    min-height: 50vh;
  }

  &.BaseBanner--min-height-75vh {
    min-height: 75vh;
  }

  &.BaseBanner--min-height-80vh {
    min-height: 80vh;
  }

  &.BaseBanner--min-height-100vh {
    min-height: 100vh;
  }

  &.BaseBanner--full-width {
    width: 100vw;
  }
}

.BaseBanner--in-sidebar {
  @apply -mx-sm sm:-mx-md md:-mx-lg xl:-mx-xl
    w-full h-auto
    lgMax:overflow-visible;

  width: calc(100% + 2 * theme('spacing.sm'));

  @screen sm {
    width: calc(100% + 2 * theme('spacing.md'));
  }

  @screen md {
    width: calc(100% + 2 * theme('spacing.lg'));
  }

  @screen xl {
    width: calc(100% + 2 * theme('spacing.xl'));
  }
}

.BaseBanner__overlay {
  @apply hidden md:block;
}

.BaseBanner__mobile-overlay {
  @apply hidden mdMax:block;
}
</style>
